import { ChangeDetectionStrategy, Component, inject, Input, OnChanges, SimpleChanges } from '@angular/core';
import { AbstractValueAccessorComponent, formatDateTime, ValueAccessorUtil } from '@railmybox/shared/util';
import { Observable } from 'rxjs';
import { PillColorType } from '@eg/ui';
import { TrainVisitModel, TrainVisitService } from '@railmybox/api-dispo';
import { TranslocoService } from '@jsverse/transloco';
import { untilDestroyed } from '@ngneat/until-destroy';

@Component({
  selector: 'rmb-trainoption-select',
  templateUrl: './trainoption-select.component.html',
  styleUrls: ['./trainoption-select.component.scss'],
  providers: [...ValueAccessorUtil.getValueAccessorProvider(TrainoptionSelectComponent)],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TrainoptionSelectComponent extends AbstractValueAccessorComponent<TrainVisitModel | string> implements OnChanges {
  private trainVisitService = inject(TrainVisitService);
  private translocoService = inject(TranslocoService);

  trainOptions$: Observable<TrainVisitModel[]>;
  selectedTrain: string;
  @Input() displayValue = 'trainIdOperator';
  @Input() clearable = true;
  @Input() dispoOrderId = '';
  @Input() extended = false;

  trainStatusColors: PillColorType = {
    ACTIVE: 'lightGreen',
    IN_PROGRESS: 'green',
    DELAYED: 'orange',
    CANCELLED: 'red',
    DONE: 'blue',
    BLOCKED: 'green',
  };

  ngOnChanges(changes: SimpleChanges) {
    super.ngOnChanges(changes);
    if (!this.dispoOrderId) {
      return;
    }
    this.trainOptions$ = this.trainVisitService.listTrainOptions(this.dispoOrderId, this.extended);
  }

  onSelect(trainOption: TrainVisitModel): void {
    this.selectedTrain = trainOption
      ? `${trainOption[this.displayValue]} ${formatDateTime(this.translocoService.getActiveLang(), 'date', trainOption.planTime)}`
      : null;
    this.value = trainOption;
    this.onChange(trainOption);
    this.changed.emit(trainOption);
  }

  async writeValue(value: TrainVisitModel | string): Promise<void> {
    if (!value) {
      this.selectedTrain = null;
      return;
    }

    super.writeValue(value);

    const trainId = typeof value === 'string' ? value : value?.id;
    await this.trainVisitService
      .adminGetTrainVisit(trainId)
      .pipe(untilDestroyed(this))
      .subscribe((train) => {
        // In case no entry found with that id
        if (!train) {
          this.changeDetectorRef.markForCheck();
          return;
        }

        this.value = train;
        this.selectedTrain = `${train[this.displayValue]} ${formatDateTime(this.translocoService.getActiveLang(), 'date', train.planTime)}`;
        this.changeDetectorRef.markForCheck();
      });
  }
}
